import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTheme } from '@mui/material/styles';
import axios from "axios";
import moment from 'moment';
import {
  Grid,
  Typography,
  Container,
  Button,
  TextField,
  Modal,
  Box,
  Tooltip
} from "@mui/material";
import { useForm, FormProvider } from 'react-hook-form';
import { any, object, string, z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorIcon from '@mui/icons-material/Error';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { resizeClientProfileImage, resizeThumbnailImage } from "../../../utils/resizeFile";
import IMG_ID from '../../../assets/img/id-icon.png';
import IMG_SELFIE_PHOTO from "../../../assets/img/selfie-icon.png";
import { useSelector } from "react-redux";
import { createBooking } from "../../../redux/actions/book";
import { SCREENING } from "../../../redux/types";

const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const Screening = ({ activeStep, onhandleNext, onhandleBack, length }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [confirmBooking, setConfirmBooking] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSelfie, setSelectedSelfie] = useState(null);
  const [uploadFile, setUploadFile] = useState();
  const [selfiePhoto, setSelfiePhoto] = useState();
  const [selfiePhotoThumbnail, setSelfiePhotoThumbnail] = useState();
  const [countryInfo, setCountryInfo] = useState('');
  const [ipInfo, setIpInfo] = useState('');

  const { bookFormTheme, bookingData, isIDUpload, isProviderReferences, isEmploymentReferences, selfieUpload } = useSelector(state => ({
    bookFormTheme: state.book.emailNotification.bookFormTheme,
    isIDUpload: state.book.emailNotification.screeningOptions.isIDUpload,
    isProviderReferences: state.book.emailNotification.screeningOptions.isProviderReferences,
    isEmploymentReferences: state.book.emailNotification.screeningOptions.isEmploymentReferences,
    selfieUpload: state.book.emailNotification.selfieUpload,
    bookingData: {
      bookFormId: state.book.bookFormId,
      bookingType: state.book.bookingType,
      perkTitle: state.book.perkTitle,
      client: state.book.client,
      preferredDate: state.book.preferredDate,
      location: state.book.location,
      duration: state.book.duration,
      message: state.book.message,
      ref1: state.book.ref1,
      ref2: state.book.ref2,
      linkedinProfile: state.book.linkedinProfile,
      companyWebsite: state.book.companyWebsite,
      idCard: state.book.idCard,
      selfiePhoto: state.book.selfiePhoto,
    }
  }));

  const screenSchema = object({
    ref1: isProviderReferences ? string().min(1, 'Your First Reference is required') : any(),
    ref2: isProviderReferences ? string().min(1, 'Your Second Reference is required') : any(),
    linkedinProfile: isEmploymentReferences ? string().regex(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Please input correct LinkedIn Profile'
    ) : any(),
    companyWebsite: isEmploymentReferences ? string().regex(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Please input correct LinkedIn Profile'
    ) : any(),
    idCard: isIDUpload ? z
      .any()
      .refine((files) => files?.length === 1, "Image is required.")
      .refine((files) => files?.[0]?.size <= MAX_FILE_SIZE, `Max file size is 5MB.`)
      .refine(
        (files) => ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type),
        ".jpg, .jpeg, .png and .webp files are accepted."
      ) : any(),
    selfiePhoto: isIDUpload && selfieUpload ? z
      .any()
      .refine((files) => files?.length === 1, "Image is required.")
      .refine((files) => files?.[0]?.size <= MAX_FILE_SIZE, `Max file size is 5MB.`)
      .refine(
        (files) => ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type),
        ".jpg, .jpeg, .png and .webp files are accepted."
      ) : any()
  });

  const getIPaddress = async () => {
    const res = await axios.get('https://geolocation-db.com/json/');
    setCountryInfo(res.data.country_name || '');
    setIpInfo(res.data?.IPv4 || '');
  };

  useEffect(() => {
    
    getIPaddress();

  }, []);

  useEffect(() => {
    methods.setValue("ref1", bookingData?.ref1);
    methods.setValue("ref2", bookingData?.ref2);
    methods.setValue("linkedinProfile", bookingData?.linkedinProfile);
    methods.setValue("companyWebsite", bookingData?.companyWebsite);
    methods.setValue("idCard", bookingData?.idCard);
    methods.setValue("selfiePhoto", bookingData?.selfiePhoto);
  }, [bookingData?.ref1, bookingData?.ref2, bookingData?.linkedinProfile, bookingData?.companyWebsite, bookingData?.idCard, bookingData?.selfiePhoto]);

  const defaultValues = {
    ref1: '',
    ref2: '',
    linkedinProfile: '',
    companyWebsite: '',
    idCard: [],
    selfiePhoto: []
  }

  const getSystemPlatformInfo = () => {
    var OSName = "Unknown";
    if (window.navigator.userAgent.indexOf("Windows NT 10.0") !== -1) OSName = "Windows 10";
    if (window.navigator.userAgent.indexOf("Windows NT 6.3") !== -1) OSName = "Windows 8.1";
    if (window.navigator.userAgent.indexOf("Windows NT 6.2") !== -1) OSName = "Windows 8";
    if (window.navigator.userAgent.indexOf("Windows NT 6.1") !== -1) OSName = "Windows 7";
    if (window.navigator.userAgent.indexOf("Windows NT 6.0") !== -1) OSName = "Windows Vista";
    if (window.navigator.userAgent.indexOf("Windows NT 5.1") !== -1) OSName = "Windows XP";
    if (window.navigator.userAgent.indexOf("Windows NT 5.0") !== -1) OSName = "Windows 2000";
    if (window.navigator.userAgent.indexOf("Mac") !== -1) OSName = "Mac/iOS";
    if (window.navigator.userAgent.indexOf("X11") !== -1) OSName = "UNIX";
    if (window.navigator.userAgent.indexOf("Linux") !== -1) OSName = "Linux";
    return OSName;
  }

  const getBrowserInfo = () => {
    var ua = window.navigator.userAgent;
    var tem;
    var M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
    if (/trident/i.test(M[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
      return 'IE ' + (tem[1] || '');
    }
    if (M[1] === 'Chrome') {
      tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
      if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera');
    }
    M = M[2] ? [M[1], M[2]] : [window.navigator.appName, window.navigator.appVersion, '-?'];
    if ((tem = ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1]);
    return M.join(' ');
  }
  const systemInfo = getSystemPlatformInfo();
  const browserInfo = getBrowserInfo();

  let client_info = {
    systemInfo,
    browserInfo,
    countryInfo,
    ipInfo
  };

  const methods = useForm({
    resolver: zodResolver(screenSchema),
    defaultValues
  });
  const { register, formState: { errors }, reset } = methods;
  const watchIdCard = methods.watch('idCard', []);
  const watchSelfiePhoto = methods.watch('selfiePhoto', []);
  useEffect(() => {
    handleSelectFile(watchIdCard);
    handleSelectSelfiePhoto(watchSelfiePhoto);
  }, [watchIdCard, watchSelfiePhoto]);

  const handleClose = () => setOpen(false);
  const handleCloseConfirmBooking = () => setConfirmBooking(false);

  const handleIDCardUpload = () => {
    document.querySelector('#IDCard-Selection').value = null;
    document.querySelector('#IDCard-Selection').click();
  }

  const handleIDCardRemove = () => {
    setSelectedFile(null);
    reset({ idCard: [] });
  }

  const handleSelectFile = files => {
    if (files.length === 0) {
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type)) {
      return;
    }
    if (files[0].size > MAX_FILE_SIZE) {
      return;
    }
    const file = files[0];
    setUploadFile(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = (e) => {
      setSelectedFile(reader.result);
    };
  }

  const handleSelfiePhotoUpload = () => {
    document.querySelector('#SelfiePhoto-Selection').value = null;
    document.querySelector('#SelfiePhoto-Selection').click();
  }
  const handleSelfiePhotoRemove = () => {
    setSelectedSelfie(null);
    reset({ selfiePhoto: [] });
  }
  const handleSelectSelfiePhoto = async files => {
    if (files.length === 0) {
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type)) {
      return;
    }
    if (files[0].size > MAX_FILE_SIZE) {
      return;
    }
    const file = files[0];
    const resizedFile = await resizeClientProfileImage(file, file.type.split('/').pop().toUpperCase());
    setSelfiePhoto(resizedFile);
    const thumbnailFile = await resizeThumbnailImage(file, file.type.split('/').pop().toUpperCase());
    setSelfiePhotoThumbnail(thumbnailFile);
    const reader = new FileReader();
    reader.readAsDataURL(resizedFile);
    reader.onloadend = (e) => {
      setSelectedSelfie(reader.result);
    };
  }

  const onBack = () => {
    const screeningData = {};
    if (isProviderReferences) {
      screeningData.ref1 = methods.getValues("ref1");
      screeningData.ref2 = methods.getValues("ref2");
    }
    if (isEmploymentReferences) {
      screeningData.linkedinProfile = methods.getValues("linkedinProfile");
      screeningData.companyWebsite = methods.getValues("companyWebsite");
    }
    if (isIDUpload) {
      screeningData.idCard = methods.getValues("idCard");
      if (selfieUpload && selfiePhoto) {
        screeningData.selfiePhoto = methods.getValues("selfiePhoto");
      }
    }
    dispatch({
      type: SCREENING,
      payload: screeningData
    });
    onhandleBack();
  };

  // ? Form Handler
  const onSubmitHandler = async (data) => {
    setOpen(false);
    setConfirmBooking(true);
  };

  const onCreateBook = async () => {
    setConfirmBooking(false);
    let formData = new FormData();
    formData.append('bookFormId', bookingData.bookFormId);
    if (isProviderReferences) {
      formData.append('ref1', methods.getValues("ref1"));
      formData.append('ref2', methods.getValues("ref2"));
    }
    if (isEmploymentReferences) {
      formData.append("linkedinProfile", methods.getValues("linkedinProfile"));
      formData.append("companyWebsite", methods.getValues("companyWebsite"));
    }
    if (isIDUpload) {
      formData.append('idCard', uploadFile);
      if (selfieUpload && selfiePhoto) {
        formData.append('selfiePhoto', selfiePhoto);
        formData.append('selfiePhotoThumbnail', selfiePhotoThumbnail);
      }
    }
    bookingData.preferredDate = moment(bookingData.preferredDate).format('YYYY/MM/DD h:mm A')
    formData.append('initialData', JSON.stringify(bookingData));
    formData.append('client_device_info', JSON.stringify(client_info));
    const { success = false } = await dispatch(createBooking(formData));
    if (success) {
      onhandleNext();
    } else {
      alert('Error happened');
    }
  };

  return (
    <Grid
      item
      display='flex'
      alignItems='center'
      justifyContent='center'
      sx={{ flexGrow: 2, flexDirection: 'column', paddingY: '5rem', backgroundColor: bookFormTheme ? '#fff' : '#151521' }}
    >
      <Container sx={{ maxWidth: { md: '650px' } }}>
        <Grid>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: bookFormTheme ? '#181c32' : '#fff' }}>Screening & Verification</Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, color: bookFormTheme ? '#777' : '#565674', mt: 0.5 }}>All clients must be screened. Your information is kept confidential.</Typography>
        </Grid>
        <FormProvider {...methods}>
          <Box
            display='flex'
            flexDirection='column'
            component='form'
            encType="multipart/form-data"
            noValidate
            autoComplete='off'
            sx={{ mt: 4 }}
            onSubmit={methods.handleSubmit(onSubmitHandler)}
          >
            {isProviderReferences &&
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={4} display="flex" alignItems="center">
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', color: bookFormTheme ? '#3f4254' : '#cdcdde' }}>Reference #1</Typography>&nbsp;
                  <Tooltip title={'Provide a reference from a provider you have seen recently.'} placement="top" enterTouchDelay={0}>
                    <ErrorIcon sx={{ color: bookFormTheme ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                  </Tooltip>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    variant="standard"
                    type='text'
                    fullWidth
                    placeholder="Your first reference (name, email & website)"
                    multiline
                    rows={2}
                    name="ref1"
                    error={!!errors['ref1']}
                    helperText={
                      errors['ref1'] ? (errors['ref1'].message) : ''
                    }
                    {...register('ref1')}
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        color: bookFormTheme != null ? bookFormTheme ? '#5e6278' : '#92929f' : null,
                        background: bookFormTheme != null ? bookFormTheme ? '#f5f8fa' : '#1b1b29' : null,
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4} display="flex" alignItems="center">
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', color: bookFormTheme ? '#3f4254' : '#cdcdde' }}>Reference #2</Typography>&nbsp;
                  <Tooltip title={'Provide another reference from a provider you have seen recently.'} placement="top" enterTouchDelay={0}>
                    <ErrorIcon sx={{ color: bookFormTheme ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                  </Tooltip>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    variant="standard"
                    type='text'
                    fullWidth
                    placeholder="Your second reference (name, email & website)"
                    multiline
                    rows={2}
                    name="ref2"
                    error={!!errors['ref2']}
                    helperText={
                      errors['ref2'] ? (errors['ref2'].message) : ''
                    }
                    {...register('ref2')}
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        color: bookFormTheme != null ? bookFormTheme ? '#5e6278' : '#92929f' : null,
                        background: bookFormTheme != null ? bookFormTheme ? '#f5f8fa' : '#1b1b29' : null,
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            }
            {isEmploymentReferences &&
              <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                <Grid item xs={4} display="flex" alignItems="center">
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', color: bookFormTheme ? '#3f4254' : '#cdcdde' }}>LinkedIn Profile</Typography>&nbsp;
                  <Tooltip title={'Please provide an active LinkedIn profile.'} placement="top" enterTouchDelay={0}>
                    <ErrorIcon sx={{ color: bookFormTheme ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                  </Tooltip>
                  <span style={{ color: 'red' }}>*</span>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    variant="standard"
                    type='text'
                    fullWidth
                    placeholder="Your LinkedIn URL"
                    name="linkedinProfile"
                    error={!!errors['linkedinProfile']}
                    helperText={errors['linkedinProfile'] ? (errors['linkedinProfile'].message) : ''}
                    {...register('linkedinProfile')}
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        color: bookFormTheme != null ? bookFormTheme ? '#5e6278' : '#92929f' : null,
                        background: bookFormTheme != null ? bookFormTheme ? '#f5f8fa' : '#1b1b29' : null,
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4} display="flex" alignItems="center">
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', color: bookFormTheme ? '#3f4254' : '#cdcdde' }}>Company Website</Typography>&nbsp;
                  <Tooltip title={'Please provide your company website displaying your corporate profile.'} placement="top" enterTouchDelay={0}>
                    <ErrorIcon sx={{ color: bookFormTheme ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                  </Tooltip>
                  <span style={{ color: 'red' }}>*</span>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    variant="standard"
                    type='text'
                    fullWidth
                    placeholder="Your Company URL"
                    name="companyWebsite"
                    error={!!errors['companyWebsite']}
                    helperText={errors['companyWebsite'] ? (errors['companyWebsite'].message) : ''}
                    {...register('companyWebsite')}
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        color: bookFormTheme != null ? bookFormTheme ? '#5e6278' : '#92929f' : null,
                        background: bookFormTheme != null ? bookFormTheme ? '#f5f8fa' : '#1b1b29' : null,
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            }
            {isIDUpload &&
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={4} display="flex" sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', color: bookFormTheme ? '#3f4254' : '#cdcdde' }}>ID Upload</Typography>&nbsp;
                  <span style={{ color: 'red' }}>*</span>
                </Grid>
                <Grid item xs={8}>
                  <div className="IDCard-warpper">
                    <Box component="img" src={selectedFile ?? IMG_ID} style={{ width: '200px', borderRadius: '6px' }} alt="IMG_ID" />
                    <Tooltip title="Upload ID File" placement="top" enterTouchDelay={0}>
                      <span className="IDCard-upload" onClick={handleIDCardUpload}>
                        <i className="fa fa-pen"></i>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove Upload" placement="top" enterTouchDelay={0}>
                      <span className="IDCard-remove " onClick={handleIDCardRemove}>
                        <i className="fa fa-remove"></i>
                      </span>
                    </Tooltip>
                  </div>
                  <input type="file" name="idCard" id="IDCard-Selection" style={{ visibility: 'hidden' }} {...register('idCard')} />
                  {errors['idCard'] && (
                    <p style={{ color: '#d32f2f', fontWeight: 400, fontSize: '0.75rem', marginInlineStart: '14px' }}>
                      {errors['idCard']?.message}
                    </p>
                  )}
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 300, marginTop: '-20px', lineHeight: '1.4', color: bookFormTheme ? '#3f4254' : '#fff' }}>Upload a clear photo of your government ID, e.g. driver's license</Typography>
                </Grid>
                {selfieUpload &&
                  <Grid item xs={4} display="flex" sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: '500', color: bookFormTheme ? '#3f4254' : '#cdcdde' }}>Selfie Upload</Typography>
                  </Grid>
                }
                {selfieUpload &&
                  <Grid item xs={8}>
                    <div className="IDCard-warpper">
                      <Box component="img" src={selectedSelfie ?? IMG_SELFIE_PHOTO} style={{ width: '200px', borderRadius: '6px' }} alt="IMG_SELFIE_PHOTO" />
                      <Tooltip title="Upload Selfie Photo" placement="top" enterTouchDelay={0}>
                        <span className="IDCard-upload" onClick={handleSelfiePhotoUpload}>
                          <i className="fa fa-pen"></i>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove Upload" placement="top" enterTouchDelay={0}>
                        <span className="IDCard-remove " onClick={handleSelfiePhotoRemove}>
                          <i className="fa fa-remove"></i>
                        </span>
                      </Tooltip>
                    </div>
                    <input type="file" name="selfiePhoto" id="SelfiePhoto-Selection" style={{ visibility: 'hidden' }} {...register('selfiePhoto')} />
                    {errors['selfiePhoto'] && (
                      <p style={{ color: '#d32f2f', fontWeight: 400, fontSize: '0.75rem', marginInlineStart: '14px' }}>
                        {errors['selfiePhoto']?.message}
                      </p>
                    )}
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 300, marginTop: '-20px', lineHeight: '1.4', color: bookFormTheme ? '#3f4254' : '#fff' }}>Please also upload a selfie with your unobstructed license beside your face.</Typography>
                  </Grid>
                }
              </Grid>
            }
          </Box>
        </FormProvider>
      </Container>
      <Grid display='flex' justifyContent='space-between' sx={{ my: 2, width: { md: '600px' } }}>
        <Button
          variant="contained"
          disabled={activeStep === 0}
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 1, mr: 1, visibility: activeStep !== 0 ? 'inherit' : 'hidden', color: '#fff', fontWeight: 600, textTransform: 'none' }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => { methods.handleSubmit(onSubmitHandler)(); setOpen(true) }}
          endIcon={<ArrowForwardIcon />}
          sx={{ mt: 1, mr: 1, color: '#fff', textTransform: 'none', fontWeight: 600 }}
        >
          {activeStep === length - 1 ? 'Finish' : 'Continue'}
        </Button>
      </Grid>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            backgroundColor: bookFormTheme ? '#fff' : '#1e1e2d',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '80%', margin: 'auto' }}>
            <HighlightOffIcon color="error" sx={{ fontSize: '7rem', margin: 'auto', display: 'flex' }} />
            <Typography id="modal-modal-description" sx={{ mt: 2, textAlign: 'center', color: bookFormTheme ? '#3f4254' : '#fff' }}>
              Sorry, looks like there are some errors detected, please try again.
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button variant="contained" sx={{ marginInlineStart: 3, fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', backgroundColor: bookFormTheme && '#f5f8fa', boxShadow: 'none' }} onClick={handleClose}>Ok, got it!</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Modal
        open={confirmBooking}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 360,
            backgroundColor: bookFormTheme ? '#fff' : '#1e1e2d',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid display="flex" flexDirection="column" alignItems="center" sx={{ width: '88%', margin: 'auto' }}>
            {/* <HighlightOffIcon color="error" sx={{ fontSize: '7rem', margin: 'auto', display: 'flex' }} /> */}
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#009ef7" }}>
              <Grid className="swal-x-content" sx={{ fontSize: '3em', color: '#009ef7' }}>?</Grid>
            </Grid>
            <Typography id="modal-modal-title" sx={{ fontSize: '1.7rem', lineHeight: '1.2', fontWeight: 700, mt: 2, textAlign: 'center', color: bookFormTheme ? '#3f4254' : '#fff' }}>
              You are about to submit this booking.
            </Typography>
            <Typography id="modal-modal-description" sx={{ fontSize: '0.85rem', mt: 2, textAlign: 'center', color: bookFormTheme ? '#3f4254' : '#fff' }}>
              Please make sure all screening and appointment details are correct.
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button
                color="success"
                variant="contained"
                sx={{ fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none', color: '#fff' }}
                onClick={onCreateBook}
              >
                Confirm
              </Button>
              <Button
                color="error"
                variant="contained"
                sx={{ marginInlineStart: 1, fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none', color: '#fff' }}
                onClick={handleCloseConfirmBooking}
              >
                Back
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Grid>
  )
}

export default Screening;
